
import React, { useEffect } from 'react';
import { GenericScheduleProps, Schedule20ReliefData, TaxReliefTransferItem, FormErrorsStep } from '../../types';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { RadioGroup } from '../ui/RadioGroup';
import { safeParseFloat } from '../../utils/calculations';

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h4 className="text-md font-semibold text-sky-700 mt-6 mb-3 border-b pb-1">{title}</h4>
);

const SubSectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h5 className="text-sm font-medium text-gray-600 mt-4 mb-2">{title}</h5>
);

const TransferItemForm: React.FC<{
  item: TaxReliefTransferItem;
  itemIndex: number;
  onItemChange: (index: number, field: keyof TaxReliefTransferItem, value: string) => void;
  onRemoveItem: (id: string) => void;
  errors: FormErrorsStep | undefined;
  t: GenericScheduleProps<Schedule20ReliefData>['t'];
  isClawbackSection: boolean;
  isBusinessRestructuring: boolean;
}> = ({ item, itemIndex, onItemChange, onRemoveItem, errors, t, isClawbackSection, isBusinessRestructuring }) => {
  return (
    <div className="p-3 border rounded-md space-y-3 bg-slate-50 relative">
      <div className="flex justify-between items-center">
        <p className="text-sm font-semibold text-gray-600">{t('sch20_counterparty_name_label')} Item {itemIndex + 1}</p>
        <Button onClick={() => onRemoveItem(item.id)} variant="danger_outline" size="sm">{t('remove_item_button')}</Button>
      </div>
      <Input label={t('sch20_counterparty_name_label')} type="text" id={`s20cpName-${item.id}`} value={item.counterpartyName} onChange={(e) => onItemChange(itemIndex, 'counterpartyName', e.target.value)} error={errors?.counterpartyName as string} />
      <Input label={t('sch20_counterparty_trn_label')} type="text" id={`s20cpTrn-${item.id}`} value={item.counterpartyTrn} onChange={(e) => onItemChange(itemIndex, 'counterpartyTrn', e.target.value)} error={errors?.counterpartyTrn as string} />
      
      {!isClawbackSection && (
        <>
          <RadioGroup label={t('sch20_transferor_transferee_label')} name={`s20transferorTransferee-${item.id}`} value={item.transferorOrTransferee} onChange={(val) => onItemChange(itemIndex, 'transferorOrTransferee', val)} options={[{label: t('sch20_transferor_option'), value: 'transferor'}, {label: t('sch20_transferee_option'), value: 'transferee'}]} t={t} error={errors?.transferorOrTransferee as string}/>
          <Input label={t('sch20_proceeds_label')} type="number" id={`s20proceeds-${item.id}`} value={item.proceeds} onChange={(e) => onItemChange(itemIndex, 'proceeds', e.target.value)} error={errors?.proceeds as string} />
          {item.transferorOrTransferee === 'transferor' && (
            <>
              <Input label={t('sch20_nbv_label')} type="number" id={`s20nbv-${item.id}`} value={item.netBookValue || ''} onChange={(e) => onItemChange(itemIndex, 'netBookValue', e.target.value)} error={errors?.netBookValue as string} />
              <Select label={t('sch20_gain_loss_in_fs_label')} id={`s20fsGainLoss-${item.id}`} value={item.gainOrLossInFS || ''} onChange={(e) => onItemChange(itemIndex, 'gainOrLossInFS', e.target.value as TaxReliefTransferItem['gainOrLossInFS'])}
                options={[
                  {value: '', label: t('select_option')},
                  {value: 'gain', label: t('sch20_gain_option')},
                  {value: 'loss', label: t('sch20_loss_option')},
                  {value: 'none', label: t('sch20_none_option')},
                ]} error={errors?.gainOrLossInFS as string}/>
              {item.gainOrLossInFS === 'gain' && <Input label={t('sch20_gain_in_fs_label')} type="number" id={`s20gainFs-${item.id}`} value={item.gainInFS || ''} onChange={(e) => onItemChange(itemIndex, 'gainInFS', e.target.value)} error={errors?.gainInFS as string} />}
              {item.gainOrLossInFS === 'loss' && <Input label={t('sch20_loss_in_fs_label')} type="number" id={`s20lossFs-${item.id}`} value={item.lossInFS || ''} onChange={(e) => onItemChange(itemIndex, 'lossInFS', e.target.value)} error={errors?.lossInFS as string} />}
            </>
          )}
          <Input label={t('sch20_date_of_transfer_label')} type="date" id={`s20dateTransfer-${item.id}`} value={item.dateOfTransfer} onChange={(e) => onItemChange(itemIndex, 'dateOfTransfer', e.target.value)} error={errors?.dateOfTransfer as string} />
        </>
      )}

      {isClawbackSection && (
        <>
          <Input label={t('sch20_date_original_transfer_label')} type="date" id={`s20dateOrigTransfer-${item.id}`} value={item.dateOfOriginalTransfer || ''} onChange={(e) => onItemChange(itemIndex, 'dateOfOriginalTransfer', e.target.value)} error={errors?.dateOfOriginalTransfer as string} />
          <Input label={t('sch20_market_value_original_label')} type="number" id={`s20mvOrig-${item.id}`} value={item.marketValueAtOriginalTransfer || ''} onChange={(e) => onItemChange(itemIndex, 'marketValueAtOriginalTransfer', e.target.value)} error={errors?.marketValueAtOriginalTransfer as string} />
          {isBusinessRestructuring ? (
             <RadioGroup label={t('sch20_transferor_natural_person_or_taxable_label')} name={`s20transferorNatOrTaxable-${item.id}`} value={item.isTransferorNaturalPersonOrStillTaxable || ''} onChange={(val) => onItemChange(itemIndex, 'isTransferorNaturalPersonOrStillTaxable', val)} options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]} t={t} error={errors?.isTransferorNaturalPersonOrStillTaxable as string}/>
          ) : ( 
            <RadioGroup label={t('sch20_transferor_still_taxable_label')} name={`s20transferorStillTaxable-${item.id}`} value={item.isTransferorStillTaxablePerson || ''} onChange={(val) => onItemChange(itemIndex, 'isTransferorStillTaxablePerson', val)} options={[{label: t('yes'), value: 'yes'}, {label: t('no'), value: 'no'}]} t={t} error={errors?.isTransferorStillTaxablePerson as string}/>
          )}
          <Input label={t('sch20_gain_clawback_label')} type="number" id={`s20gainClawback-${item.id}`} value={item.gainRecognisedClawback || ''} onChange={(e) => onItemChange(itemIndex, 'gainRecognisedClawback', e.target.value)} error={errors?.gainRecognisedClawback as string} />
          <Input label={t('sch20_loss_clawback_label')} type="number" id={`s20lossClawback-${item.id}`} value={item.lossRecognisedClawback || ''} onChange={(e) => onItemChange(itemIndex, 'lossRecognisedClawback', e.target.value)} error={errors?.lossRecognisedClawback as string} />
        </>
      )}
    </div>
  );
};


export const Schedule20_TaxRelief: React.FC<GenericScheduleProps<Schedule20ReliefData>> = ({ scheduleData, onChange, errors, t, fullFormData }) => {
  
  const handleItemChange = (
    listName: keyof Schedule20ReliefData, 
    index: number, 
    field: string, 
    value: string
  ) => {
    const list = (scheduleData[listName] as TaxReliefTransferItem[]).slice();
    list[index] = { ...list[index], [field]: value };
    onChange(listName as keyof Schedule20ReliefData, list);
  };

  const addItem = (listName: keyof Schedule20ReliefData) => {
    const list = (scheduleData[listName] as TaxReliefTransferItem[]).slice();
    const newItem: TaxReliefTransferItem = { 
      id: Date.now().toString(), 
      counterpartyName: '', 
      counterpartyTrn: '', 
      transferorOrTransferee: '', 
      proceeds: '',
      netBookValue: '',
      dateOfTransfer: '',
      gainOrLossInFS: '',
      gainInFS: '',
      lossInFS: '',
      isTransferorStillTaxablePerson: '',
      isTransferorNaturalPersonOrStillTaxable: '',
      marketValueAtOriginalTransfer: '',
      dateOfOriginalTransfer: '',
      gainRecognisedClawback: '',
      lossRecognisedClawback: '',
    };
    onChange(listName as keyof Schedule20ReliefData, [...list, newItem]);
  };

  const removeItem = (listName: keyof Schedule20ReliefData, id: string) => {
    const list = (scheduleData[listName] as TaxReliefTransferItem[]).filter(item => item.id !== id);
    onChange(listName as keyof Schedule20ReliefData, list);
  };

  useEffect(() => {
    let totalGainLossNotTaxedQG = 0;
    scheduleData.transfersWithinQualifyingGroupCurrentTaxPeriod.forEach(item => {
        if(item.transferorOrTransferee === 'transferor') {
            const gain = safeParseFloat(item.gainInFS);
            const loss = safeParseFloat(item.lossInFS);
            // If Gain in FS, that amount is NOT taxed (so it's a negative adjustment to TI, hence we sum gains)
            // If Loss in FS, that amount is NOT allowed (so it's a positive adjustment to TI, hence we subtract losses, making it add back)
            if(item.gainOrLossInFS === 'gain') totalGainLossNotTaxedQG += gain;
            else if(item.gainOrLossInFS === 'loss') totalGainLossNotTaxedQG -= loss;
        }
    });

    let totalGainLossClawbackQG = 0;
    scheduleData.transfersWithinQualifyingGroupPreviousTaxPeriodClawback.forEach(item => {
        totalGainLossClawbackQG += safeParseFloat(item.gainRecognisedClawback) - safeParseFloat(item.lossRecognisedClawback);
    });

    let totalGainLossNotTaxedBRR = 0;
    scheduleData.businessRestructuringReliefCurrentTaxPeriod.forEach(item => {
        if(item.transferorOrTransferee === 'transferor') {
            const gain = safeParseFloat(item.gainInFS);
            const loss = safeParseFloat(item.lossInFS);
            if(item.gainOrLossInFS === 'gain') totalGainLossNotTaxedBRR += gain;
            else if(item.gainOrLossInFS === 'loss') totalGainLossNotTaxedBRR -= loss;
        }
    });
    
    let totalGainLossClawbackBRR = 0;
    scheduleData.businessRestructuringReliefPreviousTaxPeriodClawback.forEach(item => {
        totalGainLossClawbackBRR += safeParseFloat(item.gainRecognisedClawback) - safeParseFloat(item.lossRecognisedClawback);
    });

    if (scheduleData.totalGainLossNotTaxedQGCurrentPeriod !== totalGainLossNotTaxedQG.toFixed(2)) {
      onChange('totalGainLossNotTaxedQGCurrentPeriod', totalGainLossNotTaxedQG.toFixed(2));
    }
    if (scheduleData.totalGainLossRecognisedClawbackQG !== totalGainLossClawbackQG.toFixed(2)) {
      onChange('totalGainLossRecognisedClawbackQG', totalGainLossClawbackQG.toFixed(2));
    }
    if (scheduleData.totalGainLossNotTaxedBRRCurrentPeriod !== totalGainLossNotTaxedBRR.toFixed(2)) {
      onChange('totalGainLossNotTaxedBRRCurrentPeriod', totalGainLossNotTaxedBRR.toFixed(2));
    }
    if (scheduleData.totalGainLossRecognisedClawbackBRR !== totalGainLossClawbackBRR.toFixed(2)) {
      onChange('totalGainLossRecognisedClawbackBRR', totalGainLossClawbackBRR.toFixed(2));
    }
  }, [
    scheduleData.transfersWithinQualifyingGroupCurrentTaxPeriod,
    scheduleData.transfersWithinQualifyingGroupPreviousTaxPeriodClawback,
    scheduleData.businessRestructuringReliefCurrentTaxPeriod,
    scheduleData.businessRestructuringReliefPreviousTaxPeriodClawback,
    onChange,
    scheduleData.totalGainLossNotTaxedQGCurrentPeriod,
    scheduleData.totalGainLossRecognisedClawbackQG,
    scheduleData.totalGainLossNotTaxedBRRCurrentPeriod,
    scheduleData.totalGainLossRecognisedClawbackBRR,
  ]);


  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-sky-600">{t('sch20_title')}</h3>
      
      <SectionHeader title={t('sch20_part_a_title')} />
      <SubSectionHeader title={t('sch20_current_period_transfers_title')} />
      {scheduleData.transfersWithinQualifyingGroupCurrentTaxPeriod.map((item, index) => (
        <TransferItemForm 
            key={item.id} 
            item={item} 
            itemIndex={index} 
            onItemChange={(idx, field, value) => handleItemChange('transfersWithinQualifyingGroupCurrentTaxPeriod', idx, field, value)} 
            onRemoveItem={(id) => removeItem('transfersWithinQualifyingGroupCurrentTaxPeriod', id)} 
            errors={(errors?.transfersWithinQualifyingGroupCurrentTaxPeriod as FormErrorsStep[] | undefined)?.[index]}
            t={t}
            isClawbackSection={false}
            isBusinessRestructuring={false}
        />
      ))}
      <Button onClick={() => addItem('transfersWithinQualifyingGroupCurrentTaxPeriod')} variant="secondary" size="sm">{t('sch20_add_transfer_item_button')}</Button>
      
      <SubSectionHeader title={t('sch20_previous_period_clawback_title')} />
      {scheduleData.transfersWithinQualifyingGroupPreviousTaxPeriodClawback.map((item, index) => (
         <TransferItemForm 
            key={item.id} 
            item={item} 
            itemIndex={index} 
            onItemChange={(idx, field, value) => handleItemChange('transfersWithinQualifyingGroupPreviousTaxPeriodClawback', idx, field, value)} 
            onRemoveItem={(id) => removeItem('transfersWithinQualifyingGroupPreviousTaxPeriodClawback', id)} 
            errors={(errors?.transfersWithinQualifyingGroupPreviousTaxPeriodClawback as FormErrorsStep[] | undefined)?.[index]}
            t={t}
            isClawbackSection={true}
            isBusinessRestructuring={false}
        />
      ))}
      <Button onClick={() => addItem('transfersWithinQualifyingGroupPreviousTaxPeriodClawback')} variant="secondary" size="sm">{t('sch20_add_transfer_item_button')}</Button>
      
      <SectionHeader title={t('sch20_part_b_title')} />
      <SubSectionHeader title={t('sch20_current_period_transfers_title')} />
      {scheduleData.businessRestructuringReliefCurrentTaxPeriod.map((item, index) => (
        <TransferItemForm 
            key={item.id} 
            item={item} 
            itemIndex={index} 
            onItemChange={(idx, field, value) => handleItemChange('businessRestructuringReliefCurrentTaxPeriod', idx, field, value)} 
            onRemoveItem={(id) => removeItem('businessRestructuringReliefCurrentTaxPeriod', id)} 
            errors={(errors?.businessRestructuringReliefCurrentTaxPeriod as FormErrorsStep[] | undefined)?.[index]}
            t={t}
            isClawbackSection={false}
            isBusinessRestructuring={true}
        />
      ))}
      <Button onClick={() => addItem('businessRestructuringReliefCurrentTaxPeriod')} variant="secondary" size="sm">{t('sch20_add_transfer_item_button')}</Button>

      <SubSectionHeader title={t('sch20_previous_period_clawback_title')} />
      {scheduleData.businessRestructuringReliefPreviousTaxPeriodClawback.map((item, index) => (
        <TransferItemForm 
            key={item.id} 
            item={item} 
            itemIndex={index} 
            onItemChange={(idx, field, value) => handleItemChange('businessRestructuringReliefPreviousTaxPeriodClawback', idx, field, value)} 
            onRemoveItem={(id) => removeItem('businessRestructuringReliefPreviousTaxPeriodClawback', id)} 
            errors={(errors?.businessRestructuringReliefPreviousTaxPeriodClawback as FormErrorsStep[] | undefined)?.[index]}
            t={t}
            isClawbackSection={true}
            isBusinessRestructuring={true}
        />
      ))}
      <Button onClick={() => addItem('businessRestructuringReliefPreviousTaxPeriodClawback')} variant="secondary" size="sm">{t('sch20_add_transfer_item_button')}</Button>


      <SectionHeader title={t('summary_label')} />
      <Input id="s20TotalGainLossNotTaxedQGCurrentPeriod" label={t('sch20_total_gain_loss_not_taxed_qg_current_label')} type="text" value={scheduleData.totalGainLossNotTaxedQGCurrentPeriod || "0.00"} readOnly className="bg-gray-100" />
      <Input id="s20TotalGainLossRecognisedClawbackQG" label={t('sch20_total_gain_loss_clawback_qg_label')} type="text" value={scheduleData.totalGainLossRecognisedClawbackQG || "0.00"} readOnly className="bg-gray-100" />
      <Input id="s20TotalGainLossNotTaxedBRRCurrentPeriod" label={t('sch20_total_gain_loss_not_taxed_brr_current_label')} type="text" value={scheduleData.totalGainLossNotTaxedBRRCurrentPeriod || "0.00"} readOnly className="bg-gray-100" />
      <Input id="s20TotalGainLossRecognisedClawbackBRR" label={t('sch20_total_gain_loss_clawback_brr_label')} type="text" value={scheduleData.totalGainLossRecognisedClawbackBRR || "0.00"} readOnly className="bg-gray-100" />

    </div>
  );
};
